// Staff Panel login (migration 0037). The token lasts 8 hours.
export const AUTH_ENDPOINTS = {
  LOGIN: '/staff/login',
  ME: '/staff/me',
  CHANGE_PASSWORD: '/staff/change-password',
  // Ends every token of the user (all devices)
  LOGOUT: '/staff/logout',
};

export const CLIENT_ENDPOINTS = {
  LIST: '/owner/clients',
  DETAILS: (id: string | number) => `/owner/clients/${id}`,
  BLOCK: (id: string | number) => `/owner/clients/${id}/block`,
  UNBLOCK: (id: string | number) => `/owner/clients/${id}/unblock`,
  // Undo of an owner soft delete (the delete itself is owner only)
  RESTORE: (id: string | number) => `/owner/clients/${id}/restore`,
  // The free trial's usage, as the election-app counts it (super_admin, support, finance)
  TRIAL_USAGE: (id: string | number) => `/owner/clients/${id}/trial-usage`,
};

export const AGENT_ENDPOINTS = {
  LIST: '/owner/agents',
  CREATE: '/owner/agents',
  DETAILS: (id: string | number) => `/owner/agents/${id}`,
  ACTIVATE: (id: string | number) => `/owner/agents/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/owner/agents/${id}/deactivate`,
  RESTORE: (id: string | number) => `/owner/agents/${id}/restore`,
};

export const PLAN_ENDPOINTS = {
  LIST: '/owner/plans',
  CREATE: '/owner/plans',
  DETAILS: (id: string | number) => `/owner/plans/${id}`,
  UPDATE: (id: string | number) => `/owner/plans/${id}`,
  ACTIVATE: (id: string | number) => `/owner/plans/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/owner/plans/${id}/deactivate`,
};

export const PROMO_CODE_ENDPOINTS = {
  LIST: '/owner/promocodes',
  CREATE: '/owner/promocodes',
  DETAILS: (id: string | number) => `/owner/promocodes/${id}`,
  UPDATE: (id: string | number) => `/owner/promocodes/${id}`,
  ACTIVATE: (id: string | number) => `/owner/promocodes/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/owner/promocodes/${id}/deactivate`,
};

// Migration 0033: the percent every partner promo code made from now on gets
export const PARTNER_CODE_SETTINGS_ENDPOINTS = {
  SETTINGS: '/owner/partner-code-settings',
};

// Migration 0036: limits of the election app's count-based free trial (append-only history)
export const TRIAL_SETTINGS_ENDPOINTS = {
  SETTINGS: '/owner/trial-settings',
};

export const PAYMENT_ENDPOINTS = {
  LIST: '/owner/payments',
  INVOICES: '/owner/invoices',
};


export const WEBSITE_FORM_ENDPOINTS = {
  QUERIES: '/owner/queries',
  PARTNER_REQUESTS: '/owner/partner-requests',
};

export const SUBSCRIPTION_ENDPOINTS = {
  LIST: '/owner/subscriptions',
};

export const VOTER_SIGN_ENDPOINTS = {
  LIST: '/owner/voter-signs',
  CREATE: '/owner/voter-signs',
  DETAILS: (id: string | number) => `/owner/voter-signs/${id}`,
  IMAGE: (id: string | number) => `/owner/voter-signs/${id}/image`,
  UPDATE: (id: string | number) => `/owner/voter-signs/${id}`,
  LEADER_PUT: (id: string | number, slot: number | string) =>
    `/owner/voter-signs/${id}/leaders/${slot}`,
  LEADER_IMAGE: (id: string | number, slot: number | string) =>
    `/owner/voter-signs/${id}/leaders/${slot}/image`,
};

export const POLLING_SCHEDULE_ENDPOINTS = {
  LIST: '/owner/polling-schedules',
  CREATE: '/owner/polling-schedules',
  DETAILS: (id: string) => `/owner/polling-schedules/${id}`,
  UPDATE: (id: string) => `/owner/polling-schedules/${id}`,
};

export const PARTNER_ENDPOINTS = {
  LIST: '/owner/partners',
  DETAILS: (id: string) => `/owner/partners/${id}`,
  PROFILE_ACTION: (id: string, action: 'approve' | 'reject' | 'suspend') =>
    `/owner/partners/${id}/${action}`,
  CHANNEL_ACTION: (
    id: string,
    channel: string,
    action: 'approve' | 'reject' | 'suspend'
  ) => `/owner/partners/${id}/channels/${channel}/${action}`,
  ORDERS: '/owner/partner-orders',
  SMS_NOTIFICATIONS: (id: string) => `/owner/partners/${id}/sms-notifications`,
  // Partner Phase Nine: Custom Promo Codes API (Owner Panel)
  CUSTOM_PROMO_CODES: (partnerId: string) => `/owner/partners/${partnerId}/promo-codes`,
  CUSTOM_PROMO_CODE_DETAIL: (partnerId: string, codeId: string) =>
    `/owner/partners/${partnerId}/promo-codes/${codeId}`,
  // Partner Phase Ten: Location Change Requests (Owner Panel)
  LOCATION_REQUESTS: '/owner/partner-location-requests',
  // Undo of an owner soft delete
  RESTORE: (id: string) => `/owner/partners/${id}/restore`,
};


// Partner Phase Ten — Location Change Requests API (Owner Panel)
export const LOCATION_REQUEST_ENDPOINTS = {
  LIST: '/owner/partner-location-requests',
  DETAILS: (id: string) => `/owner/partner-location-requests/${id}`,
  APPROVE: (id: string) => `/owner/partner-location-requests/${id}/approve`,
  REJECT: (id: string) => `/owner/partner-location-requests/${id}/reject`,
};

// Partner Phase Two & Phase Six — Commission API (Owner Panel side only)
export const COMMISSION_ENDPOINTS = {
  SETTINGS: '/owner/commission-settings',
  RULES: '/owner/commission-rules',
  RULE_DEACTIVATE: (id: string) => `/owner/commission-rules/${id}/deactivate`,
  UNCONFIGURED: '/owner/commissions/unconfigured',
  RECALCULATE: '/owner/commissions/recalculate',
  LEDGER: '/owner/commissions',
  SUMMARY: '/owner/commissions/summary',
  PAYOUTS: '/owner/payouts',
  PARTNER_PAYOUTS: (partnerId: string) => `/owner/partners/${partnerId}/payouts`,
  // Phase 6: Commission Holds
  HOLD: (entryId: string) => `/owner/commissions/${entryId}/hold`,
  RELEASE: (entryId: string) => `/owner/commissions/${entryId}/release`,
  BULK_RELEASE: (partnerId: string) => `/owner/partners/${partnerId}/commission-holds/release`,
  HOLDS_HISTORY: (entryId: string) => `/owner/commissions/${entryId}/holds`,
};

// Partner Phase Six — Reports & Exports API
export const REPORT_ENDPOINTS = {
  SUMMARY: '/owner/partner-reports/summary',
  TOP_PARTNERS: '/owner/partner-reports/top-partners',
};

export const EXPORT_ENDPOINTS = {
  CSV: (kind: string) => `/owner/exports/${kind}.csv`,
};


// Partner Phase Four — KYC API (Owner Panel side only)
export const KYC_ENDPOINTS = {
  SETTINGS: '/owner/kyc-settings',
  QUEUE: '/owner/kyc/queue',
  PARTNER_KYC: (partnerId: string) => `/owner/partners/${partnerId}/kyc`,
  VERIFY: (partnerId: string, item: string) => `/owner/partners/${partnerId}/kyc/${item}/verify`,
  REJECT: (partnerId: string, item: string) => `/owner/partners/${partnerId}/kyc/${item}/reject`,
  REVEAL: (partnerId: string, item: string) => `/owner/partners/${partnerId}/kyc/reveal/${item}`,
  DOCUMENT_URL: (partnerId: string, document: string) =>
    `/owner/partners/${partnerId}/kyc/documents/${document}/url`,
};

// Creator Channel — Social Platforms API (Owner Panel side)
export const SOCIAL_PLATFORM_ENDPOINTS = {
  LIST: '/owner/social-platforms',
  CREATE: '/owner/social-platforms',
  UPDATE: (id: string) => `/owner/social-platforms/${id}`,
};

// Material Wallet — Part B: Owner Panel Endpoints (Phase 11a)
export const WALLET_ENDPOINTS = {
  SETTINGS: '/owner/wallet/settings',
  PARTNER_WALLET: (partnerId: string) => `/owner/partners/${partnerId}/wallet`,
  PARTNER_LEDGER: (partnerId: string) => `/owner/partners/${partnerId}/wallet/ledger`,
  PARTNER_CREDIT: (partnerId: string) => `/owner/partners/${partnerId}/wallet/credit`,
  PARTNER_DEBIT: (partnerId: string) => `/owner/partners/${partnerId}/wallet/debit`,
  PARTNER_RECONCILE_RECHARGE: (partnerId: string, rechargeId: string) =>
    `/owner/partners/${partnerId}/wallet/recharges/${rechargeId}/reconcile`,
  BULK_RECONCILE_RECHARGES: '/owner/wallet/recharges/reconcile',
  RECONCILIATION: '/owner/wallet/reconciliation',
  WALLETS_LIST: '/owner/wallets',
  RECHARGES_LIST: '/owner/wallet/recharges',
  SUMMARY: '/owner/wallet/summary',
};

// Material Orders & Catalog — Part B: Owner Panel Endpoints (Phase 11b & 11c)
export const MATERIAL_ENDPOINTS = {
  PRODUCTS: '/owner/material/products',
  PRODUCT_UPDATE: (code: string) => `/owner/material/products/${code}`,
  RATES: (code: string) => `/owner/material/products/${code}/rates`,
  RATE_CREATE: (code: string) => `/owner/material/products/${code}/rates`,
  SETTINGS: '/owner/material/settings',
  ORDERS: '/owner/material/orders',
  ORDER_DETAIL: (orderId: string) => `/owner/material/orders/${orderId}`,
  ORDER_CANCEL: (orderId: string) => `/owner/material/orders/${orderId}/cancel`,
  ORDER_FAIL: (orderId: string) => `/owner/material/orders/${orderId}/fail`,
  // Phase 11c: Voter Slips, Rolls, Parts, Force Actions, Postcards, Stuck Queue
  ORDER_FILES: (orderId: string) => `/owner/material/orders/${orderId}/files`,
  ORDER_PARTS: (orderId: string) => `/owner/material/orders/${orderId}/parts`,
  ORDER_PART_LINK: (orderId: string, partIndex: number | string) =>
    `/owner/material/orders/${orderId}/parts/${partIndex}/link`,
  ORDER_FORCE_CANCEL: (orderId: string) => `/owner/material/orders/${orderId}/force-cancel`,
  ORDER_FORCE_FAIL: (orderId: string) => `/owner/material/orders/${orderId}/force-fail`,
  POSTCARDS: '/owner/material/postcards',
  POSTCARD_DETAIL: (postcardId: string) => `/owner/material/postcards/${postcardId}`,
  JOBS_STUCK: '/owner/material/jobs/stuck',
  // Migration 0034: voter data sets (counts only, never a voter) and how long unused ones are kept
  SLIP_DATA: '/owner/material/slip-data',
  SLIP_DATA_SETTINGS: '/owner/material/slip-data-settings',
  // Phase 11d: Owner Reports & Exports
  REPORTS: {
    SUMMARY: '/owner/material/reports/summary',
    WALLET: '/owner/material/reports/wallet',
    USAGE: '/owner/material/reports/usage',
    GEOGRAPHY: '/owner/material/reports/geography',
    RISK: '/owner/material/reports/risk',
  },
  EXPORTS: {
    ORDERS_CSV: '/owner/material/exports/orders.csv',
    WALLET_LEDGER_CSV: '/owner/material/exports/wallet-ledger.csv',
    WALLET_RECHARGES_CSV: '/owner/material/exports/wallet-recharges.csv',
  },
};



// Upload Requests: app Admins / Managers ask the owner to upload a ward's voter-list PDF
export const UPLOAD_REQUEST_ENDPOINTS = {
  LIST: '/owner/upload-requests',
  DETAILS: (id: string) => `/owner/upload-requests/${id}`,
  // PATCH: fix the place, wards or note of a pending request
  EDIT: (id: string) => `/owner/upload-requests/${id}`,
  UPLOAD: (id: string) => `/owner/upload-requests/${id}/upload`,
  REJECT: (id: string) => `/owner/upload-requests/${id}/reject`,
  COMPLETE: (id: string) => `/owner/upload-requests/${id}/complete`,
};

// Updates (announcements) sent to app users as a notification and kept in the app's Updates screen
export const ANNOUNCEMENT_ENDPOINTS = {
  LIST: '/owner/announcements',
  CREATE: '/owner/announcements',
  PREVIEW: '/owner/announcements/preview',
};

// Public location lists (no login needed)
// Public (no token): district names for every role, also order_ops, which may not read
// /owner/locations/districts
export const LOCATION_ENDPOINTS = {
  DISTRICTS: '/partner/locations/districts',
};

// Location master: districts, panchayat samitis and villages (staff add and rename; deletes are owner only)
export const LOCATION_MASTER_ENDPOINTS = {
  STATES: '/owner/locations/states',
  DISTRICTS: '/owner/locations/districts',
  DISTRICT: (id: number) => `/owner/locations/districts/${id}`,
  SAMITIS: '/owner/locations/samitis',
  SAMITI: (id: number) => `/owner/locations/samitis/${id}`,
  VILLAGES: '/owner/locations/villages',
  VILLAGE: (id: number) => `/owner/locations/villages/${id}`,
};

// The panel's notifications: the bell, its "view all" page and browser pushes (every login)
export const NOTIFICATION_ENDPOINTS = {
  LIST: '/panel/notifications',
  UNREAD_COUNT: '/panel/notifications/unread-count',
  READ: (id: number) => `/panel/notifications/${id}/read`,
  READ_ALL: '/panel/notifications/read-all',
  PUSH: '/panel/notifications/push',
  PUSH_SUBSCRIBE: '/panel/notifications/push/subscribe',
  PUSH_UNSUBSCRIBE: '/panel/notifications/push/unsubscribe',
  PUSH_TEST: '/panel/notifications/push/test',
};
